// src/modules/analytics/components/KpiCards.tsx
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import type { AnalyticsKpi } from '../types';

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

interface KpiCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  loading: boolean;
}

function KpiCard({ icon, value, label, loading }: KpiCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        transition: 'border-color 120ms ease-out',
        '&:hover': { borderColor: (t) => t.palette.primary.main },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            mb: 0.75,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={72} height={30} />
        ) : (
          <Typography
            sx={{
              fontSize: '1.4rem',
              fontWeight: 600,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          background: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.16 : 0.1),
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    </Paper>
  );
}

interface Props {
  kpi: AnalyticsKpi;
  loading: boolean;
}

export function KpiCards({ kpi, loading }: Props) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 1.5,
      }}
    >
      <KpiCard
        icon={<BoltOutlinedIcon fontSize="small" />}
        value={String(kpi.action_count)}
        label="Вызовов LLM"
        loading={loading}
      />
      <KpiCard
        icon={<ArrowUpwardIcon fontSize="small" />}
        value={formatTokens(kpi.tokens_in_total)}
        label="Токенов входящих"
        loading={loading}
      />
      <KpiCard
        icon={<ArrowDownwardIcon fontSize="small" />}
        value={formatTokens(kpi.tokens_out_total)}
        label="Токенов исходящих"
        loading={loading}
      />
      <KpiCard
        icon={<TimerOutlinedIcon fontSize="small" />}
        value={`${kpi.response_time_avg}ms`}
        label="Avg время ответа"
        loading={loading}
      />
    </Box>
  );
}
